import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";

import getStrain from "@/api/requests/getStrain";
import getStrainReviews from "@/api/requests/getStrainReviews";
import { DISPENSARY_DEFAULT_MAX_DISTANCE } from "@/constants/dispensary";
import { Action, Category } from "@/constants/events";
import { AddToBagProvider } from "@/context/AddToBagContext";
import { StrainPageProvider } from "@/context/StrainPageContext";
import { StrainReview } from "@/custom-types/Reviews";
import { StrainV2 } from "@/custom-types/StrainV2";
import { useEventTracker } from "@/hooks/useEventTracker";
import useStrainFeature from "@/hooks/useStrainFeature";
import useUserFavorites from "@/hooks/useUserFavorites";
import { useSelector } from "@/redux/hooks";
import { getCoordinates } from "@/redux/selectors/location";
import { getUserId } from "@/redux/selectors/user";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { extractStrainSensations } from "@/utils/extractStrainSensations";
import { throwOnRedirectHint } from "@/utils/redirectHints";
import { fetchNearbyDispensaries } from "@/utils/strains/fetchNearbyDispensaries";

import Classification from "@/components/botanic/Classification";
import StrainTile from "@/components/botanic/StrainTile";
import Breadcrumb from "@/components/Breadcrumb";
import Breadcrumbs from "@/components/Breadcrumbs";
import ButtonPlaceholder from "@/components/ButtonSkeleton";
import ComparisonShoppingSection from "@/components/ComparisonShoppingSection";
import GoogleAd from "@/components/GoogleAd";
import TerpeneIcon from "@/components/Icons/terpene.svg";
import IconSvg from "@/components/IconSvg";
import Divider from "@/components/StrainPage/Divider";
import FavoriteModal from "@/components/StrainPage/FavoriteModal";
import FavoriteButton from "@/components/StrainPage/FavoriteStrainButton";
import HeroImage from "@/components/StrainPage/HeroImage";
import ReviewCTA from "@/components/StrainPage/ReviewCTA";
import Section from "@/components/StrainPage/Section";
import ShopStrainButton from "@/components/StrainPage/ShopStrainButton";
import StrainAttributes from "@/components/StrainPage/StrainAttributes";
import StrainCannabinoids from "@/components/StrainPage/StrainCannabinoids";
import StrainDescription from "@/components/StrainPage/StrainDescription";
import StrainDispensariesSection from "@/components/StrainPage/StrainDispensariesSection";
import StrainPageMetadata from "@/components/StrainPage/StrainPageMetadata";
import StrainPageSchema from "@/components/StrainPage/StrainPageSchema";
import StrainPhotos from "@/components/StrainPage/StrainPhotos";
import StrainReviews from "@/components/StrainPage/StrainReviews";
import StrainScience from "@/components/StrainPage/StrainScience";
import StrainSensations from "@/components/StrainPage/StrainSensations";
import StrainQuizCTAPlaceholder from "@/components/StrainQuiz/StrainQuizCTAPlaceholder";
import Video from "@/components/Video";

const SOTYBannerContainer = dynamic(
  () => import("@/components/StrainPage/SOTYBanner"),
);
const StrainGrowInfo = dynamic(
  () => import("@/components/StrainPage/StrainGrowInfo"),
);
const StrainLineage = dynamic(
  () => import("@/components/StrainPage/StrainLineage"),
);
const StrainQuizContainer = dynamic(
  () => import("@/components/StrainQuiz/StrainQuizContainer"),
  {
    loading: () => <StrainQuizCTAPlaceholder className="mb-lg mt-sm" />,
  },
);
const StrainFeatureAd = dynamic(
  () => import("@/components/StrainPage/StrainFeatureAd"),
);
const StrainComparison = dynamic(() => import("@/components/StrainComparison"));

type StrainPageProps = {
  strain: StrainV2;
  reviews: StrainReview[];
};

const StrainPage: React.FC<StrainPageProps> = ({ strain, reviews }) => {
  const {
    award,
    celebrationColors,
    name,
    subtitle,
    phenotype,
    slug,
    parents,
    children,
    growInfo,
    id,
  } = strain;
  const { useInitiateFavorites } = useUserFavorites();
  const coordinates = useSelector(getCoordinates);
  useInitiateFavorites();
  const { showStrainFeature } = useStrainFeature(coordinates, strain);

  const hasLineage = parents?.length !== 0 || children?.length !== 0;

  const hasGrowInfo =
    growInfo?.averageYield &&
    growInfo?.difficulty &&
    (growInfo?.floweringDays || 0) > 0 &&
    growInfo?.height;

  const hasGrowNotes = !!growInfo?.growNotesPlain;

  const hasPhotos = Boolean(strain?.photoCount && strain.photoCount > 0);

  const userId = useSelector(getUserId);

  const router = useRouter();
  const query = useSearchParams();

  const returnToStrainPage = () => router.push(`/strains/${strain.slug}`);
  const showFavoriteModal = query?.get("show_favorites_modal");
  const favoritedRedirect = () =>
    router.push(`/strains/${strain.slug}?strainId=${strain.id}`);

  // `null` represents that the data is loading
  const [hasNearbyDispensaries, setHasNearbyDispensaries] = useState<
    boolean | null
  >(null);

  const { publishEvent } = useEventTracker();

  const strainId = id;

  useEffect(() => {
    fetchNearbyDispensaries(
      strain,
      coordinates,
      DISPENSARY_DEFAULT_MAX_DISTANCE,
    ).then((results) => {
      setHasNearbyDispensaries(results.dispensaries.length > 0);
    });
  }, [coordinates]);

  const onPhenotypeClick = () => {
    publishEvent({
      action: Action.click,
      category: Category.strainType,
      label: `${phenotype}`,
      strainId: strainId,
      userId: userId,
    });
  };

  return (
    <StrainPageProvider strain={strain} eventPage="Strain page">
      <AddToBagProvider>
        <StrainPageMetadata strain={strain} />
        {strain?.reviewCount !== 0 && (
          <StrainPageSchema strain={strain} reviews={reviews} />
        )}
        <div>
          <section className="container mt-xxl">
            <SOTYBannerContainer
              celebrationColors={celebrationColors}
              award={award}
            />
            <Header
              hasNearbyDispensaries={hasNearbyDispensaries}
              name={name}
              onPhenotypeClick={onPhenotypeClick}
              phenotype={phenotype}
              strain={strain}
              subtitle={subtitle}
            />
            {showStrainFeature !== undefined && !showStrainFeature && (
              <div
                data-testid="google-ad"
                className="flex justify-center mt-xxl lg:mb-xxl gam-rectangular-fixed-height"
              >
                <GoogleAd
                  adPath="/13212770/strain/sponsorship"
                  targeting={{
                    section: "strain",
                    strain: slug,
                    zone: "abvf",
                  }}
                  sizes={{ desktop: [728, 90], mobile: [320, 50] }}
                />
              </div>
            )}
            {showStrainFeature !== undefined && showStrainFeature && (
              <StrainFeatureAd strain={strain} />
            )}
          </section>
          <section id="strain-shopping">
            <StrainDispensariesSection />
            <StrainSensations className="container">
              <StrainQuizContainer showResults={false} />
            </StrainSensations>
            <StrainScience />
            <ComparisonShoppingSection />
            <StrainComparison />
            <Divider />
            {slug === "do-si-dos" ? (
              <div className="container mt-xxl">
                <div className="shadow-low rounded overflow-hidden">
                  <GoogleAd
                    adPath="/13212770/home/half_takeover"
                    targeting={{
                      section: "strain",
                      strain: slug,
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex justify-center mt-xxl gam-square-fixed-height">
                <GoogleAd
                  adPath="/13212770/strain/medrec_upper"
                  sizes={{ desktop: [300, 250], mobile: [300, 250] }}
                  targeting={{
                    section: "strain",
                    strain: slug,
                    zone: "abvf",
                  }}
                />
              </div>
            )}
            <Divider />
            <StrainReviews reviews={reviews} hideMetadata={true} />
            <Divider />
            <Section
              title="Strain spotlight"
              id="strain-spotlight-section"
              className="container"
            >
              <Video
                playerId="strain-page-video-player"
                playerScript="https://video.leafly.com/libraries/MCKAFIhB.js"
                playlist="https://video.leafly.com/v2/playlists/WL4FtIpb?search=__CONTEXTUAL__"
              />
            </Section>
            {(hasLineage || hasGrowInfo || hasGrowNotes) && (
              <div id="strain-lineage-and-growth-container">
                <Divider />
                <div className="container flex flex-col md:flex-row">
                  {hasLineage && <StrainLineage />}
                  {(hasGrowInfo || hasGrowNotes) && <StrainGrowInfo />}
                </div>
              </div>
            )}
            {hasPhotos && (
              <div id="strain-photos-container">
                <Divider />
                <StrainPhotos className="pt-xxl container" />
              </div>
            )}
            <Divider />
            <div className="flex justify-center mt-xxl gam-square-fixed-height mb-lg">
              <GoogleAd
                adPath="/13212770/strain/medrec_lower"
                sizes={{ desktop: [300, 250], mobile: [300, 250] }}
                targeting={{
                  section: "strain",
                  strain: slug,
                  zone: "abvf",
                }}
              />
            </div>
            <div className="container">
              <Breadcrumbs>
                <Breadcrumb href="/strains">Weed Strains</Breadcrumb>
                {!!phenotype && (
                  <Breadcrumb
                    href={`/strains/lists/category/${phenotype.toLowerCase()}`}
                  >
                    {phenotype}
                  </Breadcrumb>
                )}
                <Breadcrumb href={`/strains/${slug}`} currentLocation={true}>
                  {name}
                </Breadcrumb>
              </Breadcrumbs>
            </div>
          </section>
        </div>
        {showFavoriteModal && (
          <FavoriteModal
            aria-labelledby="add-strain-to-favorites"
            closeModal={returnToStrainPage}
            category="Reviews"
            favoritedRedirect={favoritedRedirect}
          />
        )}
      </AddToBagProvider>

      {/* This should be a sticky footer GAM ad  */}
      <GoogleAd
        adPath="/13212770/strain/strain_outofpage"
        idPrefix="div-"
        idOverride="ad-1736961463766-0"
        sizes={{ desktop: [1, 1], mobile: [1, 1] }}
        targeting={{
          section: "strain",
          strain: slug,
          zone: "abvf",
        }}
      />
    </StrainPageProvider>
  );
};

const Header: React.FC<{
  hasNearbyDispensaries: boolean | null;
  name: string;
  onPhenotypeClick: () => unknown;
  phenotype: string | null;
  strain: StrainV2;
  subtitle: string | null;
}> = ({
  hasNearbyDispensaries,
  name,
  onPhenotypeClick,
  phenotype,
  strain,
  subtitle,
}) => {
  const { topThreeTerps, feelings, flavors } = useMemo(
    () => ({
      feelings: extractStrainSensations(strain.effects).slice(0, 3),
      flavors: extractStrainSensations(strain.flavors || {}).slice(0, 3),
      topThreeTerps: Object.values(strain.terps)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3),
    }),
    [strain],
  );

  return (
    <div className="flex flex-col lg:grid lg:items-start lg:grid-cols-2 gap-x-[--grid-gutter-width]">
      <div className="order-2 lg:row-span-1 lg:col-start-2">
        <div className="flex flex-col mb-lg lg:max-w-[342px] lg:mx-auto">
          <h1 className="heading--l">{name}</h1>

          {subtitle && (
            <h2 className="text-xs font-normal text-secondary mt-xs">
              {subtitle}
            </h2>
          )}

          <div className="flex items-center mt-sm">
            {phenotype && (
              <Link
                href={`/strains/lists/category/${phenotype.toLowerCase()}`}
                onClick={onPhenotypeClick}
              >
                <Classification variant="phenotype">{phenotype}</Classification>
              </Link>
            )}

            <StrainCannabinoids />
          </div>
        </div>
      </div>

      <div className="order-1 flex items-center lg:row-span-2 lg:col-start-1 lg:items-start">
        <div className="w-3/5 relative">
          <HeroImage />
          <StrainQuizContainer showResults />
        </div>

        <div className="flex flex-col-reverse w-2/5 ml-sm">
          <ReviewCTA showRedesign />
          <div className="mb-lg">
            <FavoriteButton />
          </div>
        </div>
      </div>

      <div className="order-3 lg:row-span-2 lg:col-start-2">
        <div className="lg:max-w-[342px] lg:mx-auto">
          <div>
            {/* doing `[&>*]:mb-lg` so if there are no elements,
             it doesn't apply the margin to an empty div */}
            <div className="flex [&>*]:mb-lg">
              <StrainTile
                category={phenotype || undefined}
                name={name}
                symbol={strain.symbol}
                className="mr-[24px]"
              />

              {feelings.length > 0 && (
                <div className="mr-[24px] text-xs font-bold">
                  <StrainAttributesTitle className="mb-md">
                    Top effects
                  </StrainAttributesTitle>

                  {feelings.map(({ name }: { name: string }) => (
                    <div key={name} className="mb-md">
                      <IconSvg
                        filePath={`effects/${name.replaceAll(" ", "-").toLowerCase()}.svg`}
                        width="20"
                        height="20"
                        className="mt-[-2px] mr-xs align-middle inline-block"
                      />

                      {name}
                    </div>
                  ))}
                </div>
              )}

              {flavors.length > 0 && (
                <div className="text-xs font-bold">
                  <StrainAttributesTitle className="mb-md">
                    Top flavors
                  </StrainAttributesTitle>

                  {flavors.map(({ name }: { name: string }) => (
                    <div key={name} className="mb-md">
                      <IconSvg
                        filePath={`flavors/${name.replaceAll(" ", "-").toLowerCase()}.svg`}
                        width="20"
                        height="20"
                        className="mt-[-2px] mr-xs align-middle inline-block"
                      />

                      {name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {topThreeTerps.length > 0 && (
              <div className="mb-sm text-xs">
                <StrainAttributesTitle className="mb-sm">
                  Terpenes
                </StrainAttributesTitle>

                {topThreeTerps.map(({ name }) => (
                  <div
                    className="inline-flex relative mb-sm mr-[24px]"
                    key={name}
                  >
                    <div className="mr-sm" style={{ width: 8 }}>
                      <TerpeneIcon
                        className={`text-${name.toLowerCase()} absolute`}
                        height="100%"
                        width="8"
                      />
                    </div>
                    <div>{name}</div>
                  </div>
                ))}
              </div>
            )}

            <StrainAttributes className="!pt-0" />
          </div>

          {hasNearbyDispensaries !== false && (
            <div className="mt-[24px]">
              {hasNearbyDispensaries ? (
                <ShopStrainButton strain={strain} width="full" />
              ) : (
                <ButtonPlaceholder height="36" />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="order-4 lg:row-start-3 lg:col-start-1 max-w-[450px]">
        <StrainDescription />
      </div>
    </div>
  );
};

const StrainAttributesTitle: React.FC<
  PropsWithChildren<{ className?: string }>
> = ({ children, className }) => (
  <div className={classNames("block font-bold underline", className)}>
    <a href="#strain-sensations-section">{children}</a>
  </div>
);

export const getServerSideProps = createGetServerSideProps<StrainPageProps>(
  async (context) => {
    const { query } = context;
    const slug = Array.isArray(query?.strainSlug)
      ? query.strainSlug[0]
      : query.strainSlug || "";

    const strain = await throwOnRedirectHint(() => getStrain(slug), slug);

    if (!strain) {
      return {
        notFound: true,
      };
    }

    const { data: reviews } = await getStrainReviews(slug, 3);

    return {
      props: {
        reviews,
        strain,
      },
    };
  },
);

export default StrainPage;
